import { useRef, useState } from 'react';
import { Images, Loader2, Plus, Upload } from 'lucide-react';
import PopoverAnclado from './PopoverAnclado';

/**
 * La card punteada de "Agregar" al final de una tira de fotos, con su menú:
 * elegir una creación de Atlas, o subir un archivo.
 *
 * Vivía dentro de `FichaDeProducto.tsx`. Se extrajo cuando el editor de un post
 * del calendario necesitó la misma —el pedido fue textual: "para las imágenes
 * quiero el mismo sistema y ui de drag y de añadir que está en las tiendas
 * online" (ADR-2026-09-15-las-fotos-de-un-post-se-arrastran-como-las-de-un-producto).
 *
 * `aceptaVideo` es el único eje nuevo: el mismo control, ofreciendo también
 * videos donde el destino los recibe.
 */
export function AgregarFoto({
  onElegirGenerada,
  onSubir,
  subiendo,
  deshabilitada,
  aceptaVideo = false,
  tituloDeshabilitada = 'Ya llegó al máximo de fotos',
}: {
  onElegirGenerada: () => void;
  onSubir: (archivo: File) => void;
  subiendo: boolean;
  deshabilitada: boolean;
  /** El destino recibe video: el selector de archivos también los ofrece. */
  aceptaVideo?: boolean;
  tituloDeshabilitada?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const botonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div>
      <button
        ref={botonRef}
        type="button"
        disabled={subiendo || deshabilitada}
        onClick={() => setAbierto((v) => !v)}
        title={deshabilitada ? tituloDeshabilitada : aceptaVideo ? 'Agregar foto o video' : 'Agregar foto'}
        className="grid h-28 w-28 place-items-center rounded-xl border-2 border-dashed border-neutral-300 text-neutral-400 transition-colors hover:border-neutral-400 hover:bg-neutral-50 hover:text-neutral-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-300 disabled:hover:bg-transparent"
      >
        {subiendo ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : (
          <span className="flex flex-col items-center gap-1">
            <Plus className="w-6 h-6" />
            <span className="text-[11px]">Agregar</span>
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={aceptaVideo ? 'image/*,video/mp4,video/quicktime,video/webm' : 'image/*'}
        className="hidden"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          e.target.value = '';
          if (archivo) onSubir(archivo);
        }}
      />

      <PopoverAnclado
        abierto={abierto}
        anclaRef={botonRef}
        onCerrar={() => setAbierto(false)}
        ancho={248}
      >
        <div>
          <button
            type="button"
            onClick={() => {
              setAbierto(false);
              onElegirGenerada();
            }}
            className="flex w-full items-start gap-3 p-3 text-left hover:bg-neutral-50"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-neutral-100 text-neutral-500">
              <Images className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm text-brand-black">Elegir una creación</span>
              <span className="mt-0.5 block text-[11px] text-neutral-500">
                {aceptaVideo ? 'De lo que generaste en Atlas' : 'De las imágenes que generaste en Atlas'}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setAbierto(false);
              inputRef.current?.click();
            }}
            className="flex w-full items-start gap-3 border-t border-neutral-100 p-3 text-left hover:bg-neutral-50"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-neutral-100 text-neutral-500">
              <Upload className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm text-brand-black">
                {aceptaVideo ? 'Subir un archivo' : 'Subir una imagen'}
              </span>
              <span className="mt-0.5 block text-[11px] text-neutral-500">
                Desde tu computadora o tu teléfono
              </span>
            </span>
          </button>
        </div>
      </PopoverAnclado>
    </div>
  );
}

export default AgregarFoto;
